import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import "./StaffEdit.css";
import StaffEditorForm from "../components/staff/StaffEditorForm";
import StaffServicesAssignment from "../components/staff/StaffServicesAssignment";
import StaffKpiProgressCard from "../components/staff/StaffKpiProgressCard";
import StaffTrainingProfileCard from "../components/staff/StaffTrainingProfileCard";
import StaffClientSatisfactionCard from "../components/staff/StaffClientSatisfactionCard";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  getStaff,
  getStaffKpiProgress,
  getStaffLogin,
  removeStaffPhoto,
  getStaffSchedule,
  resetStaffCredentials,
  uploadStaffPhoto,
  updateStaff,
  updateStaffSchedule,
} from "../api/staffs";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";

import {

  getUserPermissions,

  resetUserPermissions,

  updateUserPermissions,

} from "../api/salonPermissions";

import { listSalonUsers, updateSalonUser } from "../api/salonUsers";

import { SALON_ROLE_OPTIONS, normalizeSalonRole, roleLabel } from "../utils/salonRoles";
import { formatPermissionError } from "../utils/permissionErrorMessages";



const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "solo_technician",
  "super_admin",
  "superadmin",
];
const OWNER_REQUIRED_ERROR = "At least one user with Owner role is required for the salon.";
const PRIMARY_OWNER_ROLE_LOCKED_ERROR =
  "The primary owner role is locked for this salon. Transfer salon ownership first if you need to change this role.";


const defaultWeeklySchedule = () =>

  Array.from({ length: 7 }).map((_, idx) => ({

    day_of_week: idx,

    is_working: false,

    start_time: null,

    end_time: null,

  }));



const snapTo15 = (hhmm) => {

  if (!hhmm) return null;

  const [h, m] = hhmm.split(":");

  const hh = parseInt(h, 10);

  const mm = parseInt(m, 10);

  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  let snapped = Math.round(mm / 15) * 15;

  let hour = hh;

  if (snapped === 60) {

    snapped = 0;

    hour = (hour + 1) % 24;

  }

  return `${String(hour).padStart(2, "0")}:${String(snapped).padStart(2, "0")}`;

};



const PERMISSION_GROUPS = [
  {
    title: "Scheduling",
    items: [
      { key: "schedule.view_all", label: "View the full schedule" },
      { key: "schedule.view_own", label: "View your own schedule" },
      { key: "schedule.edit", label: "Create or edit appointments" },
      { key: "schedule.cancel", label: "Cancel appointments" },
    ],
  },
  {
    title: "Time Clock",
    items: [
      { key: "timeclock.view", label: "View and stamp time clocks" },
      { key: "timeclock.edit", label: "Edit time clock entries" },
      { key: "timeclock.delete", label: "Delete time clock entries" },
    ],
  },
  {
    title: "Clients",
    items: [
      { key: "clients.manage", label: "Manage client records" },
      { key: "clients.view_contact", label: "View client contact info" },
      { key: "clients.view_metrics", label: "View client visit and spend metrics" },
    ],
  },
  {
    title: "Emails",
    items: [
      {
        key: "emails.manage_opt_outs",
        label: "Manage email opt-outs and preferences",
      },
    ],
  },
  {
    title: "Tickets & Services",
    items: [
      { key: "tickets.manage", label: "Manage tickets" },
      { key: "tickets.delete", label: "Delete tickets" },
      { key: "tickets.reopen", label: "Re-open tickets" },
      { key: "tickets.refund", label: "Refund tickets" },
      { key: "services.manage", label: "Manage services" },
    ],
  },
  {
    title: "Workspace Rentals",
    items: [
      { key: "rentals.view", label: "View workspace rentals" },
      { key: "rentals.manage", label: "Manage workspace rentals" },
      { key: "rentals.marketplace_manage", label: "Manage marketplace listings" },
      { key: "rentals.reports.view", label: "View rental reports" },
    ],
  },
  {
    title: "Staff",
    items: [{ key: "staff.manage", label: "Manage staff members" }],
  },
  {
    title: "Admin",
    items: [
      { key: "settings.manage", label: "Manage salon settings" },
      { key: "users.manage_roles", label: "Manage user roles" },
    ],
  },
  {
    title: "Rewards",
    items: [{ key: "rewards.view", label: "View rewards program" }],
  },
  {
    title: "Reports",
    items: [
      { key: "reports.view", label: "View reports" },
      { key: "schedule.estimated_revenue", label: "View estimated revenue" },
    ],
  },
  {
    title: "Payroll",
    items: [
      { key: "payroll.view", label: "View payroll" },
      { key: "payroll.manage", label: "Manage payroll and pay stubs" },
    ],
  },
  {
    title: "Training & Development",
    items: [
      { key: "training.view", label: "View training" },
      { key: "training.manage", label: "Manage training" },
    ],
  },
];


const OWNER_ONLY_PERMISSIONS = ["settings.manage", "users.manage_roles"];



const STAFF_ONLY_ROLE_OPTIONS = [

  { value: "technician", label: roleLabel("technician") },

  { value: "apprentice", label: roleLabel("apprentice") },

];



const SALON_ROLE_DROPDOWN_OPTIONS = SALON_ROLE_OPTIONS.map((value) => ({
  value,
  label: roleLabel(value),
}));
const HELP_ALLOWED_ROLES = new Set(["owner", "manager", "super_admin", "superadmin"]);

const STAFF_GUIDE_TROUBLESHOOTING = [
  {
    title: "Staff not showing on schedule",
    body: "Confirm Weekly Schedule has working hours, the staff profile is active, and role permissions allow schedule visibility.",
  },
  {
    title: "Staff can’t be assigned to ticket services",
    body: "Check Schedule and Time Off for the appointment time. Staff must be working and not blocked off during that time window.",
  },
  {
    title: "Online booking not showing this staff",
    body: "Verify services are assigned and marked to show online, and confirm the staff is available during the requested booking hours.",
  },
];


const resolveLinkedUserId = (staffRecord) => {

  if (!staffRecord) return null;

  if (staffRecord.user_id) return staffRecord.user_id;

  if (staffRecord.userId) return staffRecord.userId;

  if (staffRecord.user?.id) return staffRecord.user.id;

  if (staffRecord.user?.user_id) return staffRecord.user.user_id;

  if (staffRecord.user?.userId) return staffRecord.user.userId;

  return null;

};



const resolveLinkedIdList = (values, fallbackValue = null) => {

  const rawValues = Array.isArray(values) ? values : [];

  const normalized = rawValues
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value));

  if (normalized.length > 0) {

    return Array.from(new Set(normalized)).sort((a, b) => a - b);

  }

  const fallback = Number(fallbackValue);

  return Number.isInteger(fallback) ? [fallback] : [];

};

const joinFullName = (firstName, lastName) =>
  [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");

export function StaffEditPanel({
  staffId,

  embedded = false,

  onDone,

  onCancel,

}) {

  const navigate = useNavigate();

  const { token, role: editorRole, user: currentUser, updateCurrentUser } = useAuth();
  const [staff, setStaff] = useState(null);

  const [weeklySchedule, setWeeklySchedule] = useState(defaultWeeklySchedule());

  const [loading, setLoading] = useState(true);

  const [savingInfo, setSavingInfo] = useState(false);

  const [savingSchedule, setSavingSchedule] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const [saveStatus, setSaveStatus] = useState(null);

  const saveStatusTimeoutRef = useRef(null);

  const [linkedUserId, setLinkedUserId] = useState(null);
  const [kpiProgress, setKpiProgress] = useState(null);
  const [loadingKpiProgress, setLoadingKpiProgress] = useState(false);
  const [kpiProgressError, setKpiProgressError] = useState("");
  const [savingKpiTargets, setSavingKpiTargets] = useState(false);
  const [membership, setMembership] = useState(null);
  const [staffPermissionToggles, setStaffPermissionToggles] = useState({
    enableCancelAppointments: false,
    enableEstimatedRevenue: false,
    enableSalonSpeakModules: false,
  });
  const [loadingStaffPermissionToggles, setLoadingStaffPermissionToggles] =
    useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginStatus, setLoginStatus] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const canEditScheduleOrder = ["owner", "manager"].includes(
    (editorRole || "").toLowerCase()
  );
  const canViewStaffHelp = HELP_ALLOWED_ROLES.has((editorRole || "").toLowerCase());
  const sectionHighlightTimeoutRef = useRef(null);
  const linkedSalonIds = useMemo(
    () => resolveLinkedIdList(staff?.linked_salon_ids, staff?.salon_id),
    [staff?.linked_salon_ids, staff?.salon_id]
  );
  const linkedBusinessIds = useMemo(
    () => resolveLinkedIdList(staff?.linked_business_ids),
    [staff?.linked_business_ids]
  );


  const loadData = useCallback(

    async ({ suppressLoading = false } = {}) => {

      if (!staffId) {

        setStaff(null);

        if (!suppressLoading) {

          setLoading(false);

        }

        return;

      }

      if (!suppressLoading) {

        setLoading(true);

      }

      setError("");

      setSaveStatus(null);

      try {

        const staffData = await getStaff(staffId, token);

        const staffLinkedUserId = resolveLinkedUserId(staffData);

        setStaff(staffData);

        setLinkedUserId(staffLinkedUserId);

        setLoginEmail(staffData.email || "");

        if (staffData.user_id) {

          try {

            const loginData = await getStaffLogin(staffId, token);

            setLoginUsername(loginData.username || "");

            setLoginEmail(loginData.email || staffData.email || "");

          } catch (err) {

            setLoginUsername((prev) => prev || staffData.email || "");

          }

        } else {

          setLoginUsername("");

        }

        setLoginStatus(null);

        const scheduleData = await getStaffSchedule(staffId, token);

        const sanitizedSchedule =

          scheduleData?.weekly_schedule && Array.isArray(scheduleData.weekly_schedule)

            ? scheduleData.weekly_schedule.map((item) => ({

              ...item,

              start_time: snapTo15(item.start_time),

              end_time: snapTo15(item.end_time),

            }))

            : defaultWeeklySchedule();

        setWeeklySchedule(sanitizedSchedule);

      } catch (err) {

        setError(err.message || "Failed to load data");

      } finally {

        if (!suppressLoading) {

          setLoading(false);

        }

      }

    },

    [staffId, token]

  );



  useEffect(() => {

    loadData();

  }, [loadData]);

  const loadKpiProgress = useCallback(async () => {
    if (!staffId) {
      setKpiProgress(null);
      setKpiProgressError("");
      return;
    }
    setLoadingKpiProgress(true);
    setKpiProgressError("");
    try {
      const data = await getStaffKpiProgress(staffId, token);
      setKpiProgress(data);
    } catch (err) {
      setKpiProgress(null);
      setKpiProgressError(err?.message || "Unable to load KPI progress.");
    } finally {
      setLoadingKpiProgress(false);
    }
  }, [staffId, token]);

  useEffect(() => {
    loadKpiProgress();
  }, [loadKpiProgress]);


  useEffect(() => {

    if (!success) return;

    const timer = setTimeout(() => setSuccess(""), 3000);

    return () => clearTimeout(timer);

  }, [success]);



  const loadMembership = useCallback(async () => {

    if (!linkedUserId) {

      setMembership(null);

      return;

    }

    try {

      const list = await listSalonUsers(token);

      const target = Array.isArray(list)

        ? list.find((member) => member.user_id === linkedUserId)

        : null;

      setMembership(target || null);

    } catch (err) {

      setMembership(null);

    }

  }, [linkedUserId, token]);



  useEffect(() => {
    if (!linkedUserId) {
      setMembership(null);
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
      return;
    }
    loadMembership();
  }, [linkedUserId, loadMembership]);

  const loadStaffPermissionToggles = useCallback(async () => {
    if (!linkedUserId) {
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
      return;
    }
    setLoadingStaffPermissionToggles(true);
    try {
      const bundle = await getUserPermissions(linkedUserId, token);
      setStaffPermissionToggles({
        enableCancelAppointments: Boolean(bundle?.effective?.["schedule.cancel"]),
        enableEstimatedRevenue: Boolean(
          bundle?.effective?.["schedule.estimated_revenue"]
        ),
        enableSalonSpeakModules: Boolean(bundle?.effective?.["training.view"]),
      });
    } catch (err) {
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
    } finally {
      setLoadingStaffPermissionToggles(false);
    }
  }, [linkedUserId, token]);

  useEffect(() => {
    loadStaffPermissionToggles();
  }, [loadStaffPermissionToggles]);


  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
      if (sectionHighlightTimeoutRef.current) {
        clearTimeout(sectionHighlightTimeoutRef.current);
      }
    };
  }, []);


  const staffForForm = useMemo(() => {

    if (!staff) return null;

    const roleValue = membership?.role ?? staff.role;

    return { ...staff, role: normalizeSalonRole(roleValue) };

  }, [staff, membership?.role]);



  const activeRoleOptions = useMemo(

    () => (linkedUserId ? SALON_ROLE_DROPDOWN_OPTIONS : STAFF_ONLY_ROLE_OPTIONS),

    [linkedUserId]

  );



  const isPrimaryOwnerMembership = Boolean(membership?.is_primary_owner);

  const modalText = "text-base md:text-lg";
  const labelText =

    "text-sm md:text-base font-semibold tracking-wide text-slate-500";

  const fieldClass =

    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300";

  const btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full";

  const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";

  const sectionTitleClass = "text-lg md:text-xl font-semibold text-slate-900";



  const handleStaffSave = async ({ info, permissionToggles }) => {
    if (!staffId || !info?.display_name?.trim()) return;
    setSavingInfo(true);
    setError("");

    setSuccess("");

    try {

      const { role, ...rest } = info;

      const normalizedRole = normalizeSalonRole(role);

      const staffPayload = { ...rest };

      if (!linkedUserId) {

        staffPayload.role = normalizedRole;

      }



      await updateStaff(staffId, staffPayload, token);

      const nextFullName = joinFullName(info.first_name, info.last_name);
      if (

        linkedUserId &&

        currentUser?.id &&

        linkedUserId === currentUser.id &&

        nextFullName
      ) {

        updateCurrentUser({

          full_name: nextFullName,
          name: nextFullName,
        });

      }

      if (linkedUserId) {
        const membershipRole = membership
          ? normalizeSalonRole(membership.role)
          : null;
        if (membershipRole !== normalizedRole) {
          await updateSalonUser(linkedUserId, { role: normalizedRole }, token);
          await loadMembership();
        }
      }
      if (linkedUserId && permissionToggles) {
        const latestBundle = await getUserPermissions(linkedUserId, token);
        const nextOverrides = { ...(latestBundle?.overrides || {}) };

        const applyPermissionToggle = (key, enabled) => {
          const defaultValue = Boolean(latestBundle?.defaults?.[key]);
          const nextValue = Boolean(enabled);
          if (nextValue === defaultValue) {
            delete nextOverrides[key];
            return;
          }
          nextOverrides[key] = nextValue;
        };

        applyPermissionToggle(
          "schedule.cancel",
          permissionToggles.enableCancelAppointments
        );
        applyPermissionToggle(
          "schedule.estimated_revenue",
          permissionToggles.enableEstimatedRevenue
        );
        applyPermissionToggle(
          "training.view",
          permissionToggles.enableSalonSpeakModules
        );

        const permissionResult = await updateUserPermissions(
          linkedUserId,
          nextOverrides,
          token
        );
        setStaffPermissionToggles({
          enableCancelAppointments: Boolean(
            permissionResult?.effective?.["schedule.cancel"]
          ),
          enableEstimatedRevenue: Boolean(
            permissionResult?.effective?.["schedule.estimated_revenue"]
          ),
          enableSalonSpeakModules: Boolean(
            permissionResult?.effective?.["training.view"]
          ),
        });
      }
      setSaveStatus({ type: "success", message: "Changes have been saved." });
      if (saveStatusTimeoutRef.current) {

        clearTimeout(saveStatusTimeoutRef.current);

      }

      saveStatusTimeoutRef.current = setTimeout(() => {

        setSaveStatus(null);

      }, 3000);

      setSuccess("Changes have been saved.");

      await loadData({ suppressLoading: true });

      onDone?.();

    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailCode = typeof detail === "object" ? detail?.code : detail;
      const detailMessage = typeof detail === "object" ? detail?.message : detail;
      const message =
        detailCode === "LAST_OWNER_REQUIRED"
          ? OWNER_REQUIRED_ERROR
          : detailMessage === "Primary owner membership cannot be modified"
          ? PRIMARY_OWNER_ROLE_LOCKED_ERROR
          : detailMessage === "Managers cannot modify owners"
          ? "Managers cannot change the role of an owner."
          : detailMessage === "Managers cannot promote members to owner"
          ? "Managers cannot assign the owner role."
          : detailMessage || err.message || "Failed to save staff info";
      setSaveStatus({ type: "error", message });
    } finally {
      setSavingInfo(false);
    }
  };


  const handleSaveKpiTargets = async (payload) => {
    if (!staffId) return;
    setSavingKpiTargets(true);
    setError("");
    setSuccess("");
    try {
      const updated = await updateStaff(staffId, payload, token);
      setStaff(updated);
      await loadKpiProgress();
      setSuccess("KPI targets updated.");
    } catch (err) {
      setError(err?.message || "Unable to save KPI targets.");
    } finally {
      setSavingKpiTargets(false);
    }
  };


  const handleSaveSchedule = async (schedule) => {
    if (!staffId || !Array.isArray(schedule)) return;
    setSavingSchedule(true);

    setError("");

    setSuccess("");

    try {

      await updateStaffSchedule(staffId, schedule, token);

      setSuccess("Changes have been saved.");

      await loadData({ suppressLoading: true });

      onDone?.();

    } catch (err) {

      setError(err?.response?.data?.detail || err.message || "Failed to save schedule");

    } finally {

      setSavingSchedule(false);

    }

  };



  const handleUpdateLogin = async () => {
    if (!staffId || !staff?.user_id) return;
    if (newPassword && newPassword !== confirmPassword) {

      setLoginStatus({ type: "error", message: "Passwords do not match." });

      return;

    }

    setLoginStatus(null);

    try {

      const trimmedEmail = loginEmail.trim().toLowerCase();

      const currentStaffEmail = (staff?.email || "").trim().toLowerCase();

      if (trimmedEmail && trimmedEmail !== currentStaffEmail) {

        await updateStaff(staffId, { email: trimmedEmail }, token);

      }

      const payload = {
        username: loginUsername?.trim() ?? "",

      };

      if (newPassword) {

        payload.password = newPassword;

      }

      await resetStaffCredentials(staffId, payload, token);

      setLoginStatus({ type: "success", message: "Login updated." });

      setNewPassword("");

      setConfirmPassword("");

      await loadData({ suppressLoading: true });

    } catch (err) {

      const detail = err?.response?.data?.detail;

      const message =

        detail === "USERNAME_TAKEN"

          ? "That username is already taken."

          : detail === "EMAIL_ALREADY_IN_USE"

          ? "That email address is already assigned to another team member. Please use a different email address."

          : detail ||
          err?.response?.data?.message ||

          err?.message ||

          "Unable to update login credentials.";

      setLoginStatus({ type: "error", message });

    }
  };

  const handleUploadPhoto = useCallback(
    async (file) => {
      if (!staffId || !file) return "";
      const result = await uploadStaffPhoto(staffId, file, token);
      const nextUrl = result?.photo_url || "";
      setStaff((prev) => (prev ? { ...prev, photo_url: nextUrl } : prev));
      return nextUrl;
    },
    [staffId, token]
  );

  const handleRemovePhoto = useCallback(async () => {
    if (!staffId) return "";
    const result = await removeStaffPhoto(staffId, token);
    const nextUrl = result?.photo_url || "";
    setStaff((prev) => (prev ? { ...prev, photo_url: nextUrl } : prev));
    return nextUrl;
  }, [staffId, token]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (sectionHighlightTimeoutRef.current) {
        clearTimeout(sectionHighlightTimeoutRef.current);
      }
      sectionHighlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const helpSections = useMemo(() => {
    const sections = [];
    if (staff?.user_id) {
      sections.push({
        id: "login",
        title: "Login",
        description: "How this staff member signs in and how you maintain secure access.",
        anchorId: "section-login",
        bullets: [
          "Staff can sign in with username and email if your setup supports both.",
          "This login email is synced with Staff Email in Details.",
          "Use passwords with at least 8 characters and reset during turnover.",
          "Do not share a single login across multiple team members.",
        ],
        tips: [
          "If login fails, confirm username/email first before resetting password.",
          "Use unique credentials so audit history stays accurate.",
        ],
      });
    }
    sections.push(
      {
        id: "details",
        title: "Details",
        description: "Core identity and role settings used throughout Ellyanna.",
        anchorId: "section-details",
        bullets: [
          "Display Name is what front desk and technicians see on schedule and tickets.",
          "Role controls permission scope and reporting access.",
          "Email and phone support communication and account recovery.",
          "Choose the closest role to actual daily responsibilities.",
        ],
        tips: [
          "Avoid nickname changes that confuse clients and staff.",
          "Review role after promotions to avoid missing permissions.",
        ],
      },
      {
        id: "settings",
        title: "Settings",
        description: "Feature toggles that change how the staff member appears in operations.",
        anchorId: "section-settings",
        bullets: [
          "Use toggles to enable staff-specific tools such as Time Clock participation.",
          "Deactivate instead of deleting when staff leave to preserve history.",
          "A deactivated staff profile should stop new schedule booking and login access while keeping prior records.",
          "Historical reports remain intact even when active settings change.",
        ],
        tips: [
          "If a staff member is missing in a tool, verify toggle settings first.",
        ],
      },
      {
        id: "profile",
        title: "Profile",
        description: "Optional profile details used for internal and client-facing context.",
        anchorId: "section-profile",
        bullets: [
          "Use profile fields to document skills, languages, and experience.",
          "Keep profile notes professional and business-relevant.",
          "Avoid sensitive personal data that is not needed for operations.",
        ],
        tips: [
          "Profile quality helps bookings route to the right technician.",
        ],
      },
      {
        id: "schedule",
        title: "Schedule",
        description: "Working hours and availability logic that powers booking decisions.",
        anchorId: "section-schedule",
        bullets: [
          "Set weekly working hours and keep them current.",
          "Not Working or Time Off blocks appointment placement during those windows.",
          "Availability here directly affects ticket service assignment validation.",
        ],
        tips: [
          "When assignments fail, check overlapping time off first.",
        ],
      },
      {
        id: "services",
        title: "Services",
        description: "Controls what this technician can perform and what appears in booking flows.",
        anchorId: "section-services",
        bullets: [
          "Assign only services this staff member can perform confidently.",
          "Assigned services drive online booking choices and front desk options.",
          "Keep durations and overrides accurate to avoid schedule conflicts.",
        ],
        tips: [
          "Service assignment mismatches are a common cause of wrong bookings.",
        ],
      }
    );

    if (linkedUserId) {
      sections.push({
        id: "permissions",
        title: "Permissions",
        description: "Fine-tune what this user can see and change in the system.",
        anchorId: "section-permissions",
        bullets: [
          "Permission categories map to areas such as schedule, tickets, reports, and settings.",
          "Start from role defaults and only add overrides when necessary.",
          "Use least-privilege access for security and clearer accountability.",
        ],
        tips: [
          "Grant access in small increments and retest workflows immediately.",
        ],
      });
    }

    return sections;
  }, [linkedUserId, staff?.user_id]);


  const content = (
    <div className={`${modalText} space-y-6`}>

      {saveStatus?.type === "success" && (

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm">

          {saveStatus.message}

        </div>

      )}

      {saveStatus?.type === "error" && (

        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm flex items-start justify-between gap-3">

          <div className="flex items-start gap-2">

            <span>{saveStatus.message}</span>

          </div>

          <button

            type="button"

            className="text-xs text-rose-700/80 hover:text-rose-800 underline"

            onClick={() => setSaveStatus(null)}

          >

            Dismiss

          </button>

        </div>

      )}

      {error && saveStatus?.type !== "error" && (

        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm flex items-start gap-2">

          <span>{error}</span>

        </div>

      )}

      {success && (

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm">

          {success}

        </div>

      )}



      {staff?.user_id && (
        <div
          id="section-login"
          className={`${cardClass} scroll-mt-24 ${highlightedSectionId === "section-login" ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200" : ""}`}
        >
          <div className="mb-4">
            <div className={sectionTitleClass}>
              Login Credentials
            </div>
            <div className="text-sm md:text-base text-slate-500">
              Manage how this staff member signs in.
            </div>
            {canViewStaffHelp && (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                Tip: Use unique credentials for each staff member so security and audit logs stay accurate.
              </div>
            )}
          </div>


          {loginStatus?.type === "error" && (

            <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-rose-700 text-sm">

              {loginStatus.message}

            </div>

          )}

          {loginStatus?.type === "success" && (

            <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-700 text-sm">

              {loginStatus.message}

            </div>

          )}



          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_username"
                className={`${labelText} flex items-center gap-2`}
              >
                Username
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Staff can log in with username or email."
                    label="Username"
                    maxWidth={260}
                  />
                )}
              </label>
              <input
                id="login_username"

                name="login_username"

                autoComplete="username"

                type="text"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={loginUsername}

                onChange={(e) => setLoginUsername(e.target.value)}

                placeholder="username"

              />

              <div className="text-xs md:text-sm text-slate-500">

                User can log in with username or email.

              </div>

            </div>



            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_email"
                className={`${labelText} flex items-center gap-2`}
              >
                Email
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="This email is used for login and staff notifications."
                    label="Email"
                    maxWidth={260}
                  />
                )}
              </label>
              <input
                id="login_email"

                name="login_email"

                autoComplete="email"

                type="email"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={loginEmail}

                onChange={(e) => setLoginEmail(e.target.value)}

                placeholder="email@example.com"

              />

              <div className="text-xs md:text-sm text-slate-500">

                This email is kept in sync with Staff Email.

              </div>

            </div>



            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_new_password"
                className={`${labelText} flex items-center gap-2`}
              >
                New Password
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Leave blank to keep the current password."
                    label="New Password"
                    maxWidth={260}
                  />
                )}
              </label>
              <input

                id="login_new_password"

                name="login_new_password"

                autoComplete="new-password"

                type="password"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={newPassword}

                onChange={(e) => setNewPassword(e.target.value)}

                placeholder="At least 8 characters"

              />

            </div>



            <div className="flex flex-col gap-1">

              <label

                htmlFor="login_confirm_password"

                className={labelText}

              >

                Confirm Password

              </label>

              <input

                id="login_confirm_password"

                name="login_confirm_password"

                autoComplete="new-password"

                type="password"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={confirmPassword}

                onChange={(e) => setConfirmPassword(e.target.value)}

                placeholder="Repeat password"

              />

            </div>

          </div>



          <div className="mt-4 flex items-center justify-end">

            <button

              type="button"

              className={`${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800`}

              onClick={handleUpdateLogin}

            >

              Update Login

            </button>

          </div>

        </div>

      )}



      <StaffEditorForm
        mode="edit"
        initialStaff={staffForForm}
        initialSchedule={weeklySchedule}
        onSave={handleStaffSave}
        onSaveSchedule={handleSaveSchedule}
        saving={savingInfo}
        savingSchedule={savingSchedule}
        error=""
        showCancel={!!onCancel}
        onCancel={onCancel}
        compact={false}
        roleOptions={activeRoleOptions}
        roleLocked={isPrimaryOwnerMembership}
        roleLockNote={
          isPrimaryOwnerMembership
            ? "Primary owner role is locked for this salon. Transfer ownership first if you need another owner to manage this location."
            : ""
        }
        canEditScheduleOrder={canEditScheduleOrder}
        onUploadPhoto={handleUploadPhoto}
        onRemovePhoto={handleRemovePhoto}
        highlightSectionId={highlightedSectionId}
        showInlineHelp={canViewStaffHelp}
        showTechnicianPermissionToggles={Boolean(linkedUserId)}
        permissionToggles={staffPermissionToggles}
        loadingPermissionToggles={loadingStaffPermissionToggles}
        beforeProfileContent={
          <>
            {staffId ? (
              <StaffKpiProgressCard
                title="Targeted KPI"
                subtitle="Set per-staff KPI targets and track current month achievement before reviewing training and development."
                progress={kpiProgress}
                loading={loadingKpiProgress}
                error={kpiProgressError}
                editable
                initialTargets={staff}
                onSaveTargets={handleSaveKpiTargets}
                saving={savingKpiTargets}
                cardClass={cardClass}
              />
            ) : null}
            {linkedUserId ? (
              <StaffTrainingProfileCard
                userId={linkedUserId}
                token={token}
                cardClass={cardClass}
              />
            ) : null}
            {staffId ? (
              <StaffClientSatisfactionCard
                staffId={staffId}
                token={token}
                cardClass={cardClass}
              />
            ) : null}
          </>
        }
        uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
      />
      <div
        id="section-services"
        className={`scroll-mt-24 ${highlightedSectionId === "section-services" ? "ring-2 ring-rose-300 ring-offset-2 rounded-2xl transition-shadow duration-200" : ""}`}
      >
        <StaffServicesAssignment
          staffId={staffId}
          salonId={staff?.salon_id}
          token={token}
          editorRole={editorRole}
          highlightSectionId={highlightedSectionId}
          showInlineHelp={canViewStaffHelp}
          uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
        />
      </div>
      {linkedUserId && (
        <div
          id="section-permissions"
          className={`scroll-mt-24 ${highlightedSectionId === "section-permissions" ? "ring-2 ring-rose-300 ring-offset-2 rounded-2xl transition-shadow duration-200" : ""}`}
        >
          <StaffPermissionsSection
            userId={linkedUserId}
            editorRole={editorRole}
            token={token}
            highlightSectionId={highlightedSectionId}
            showInlineHelp={canViewStaffHelp}
            onSaved={() => {
              loadMembership();
              loadStaffPermissionToggles();
            }}
            uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
          />
        </div>
      )}
    </div>

  );



  if (embedded) {

    if (loading) {

      return <div className="text-slate-600">Loading...</div>;

    }

    if (!staff) {

      return <div className="text-slate-600">Staff not found.</div>;

    }

    return <div className="space-y-6">{content}</div>;

  }



  const headerLine = staff

    ? `Editing ${staff.display_name || "Staff"}`

    : loading

      ? "Loading staff details"

      : "Staff not found";



  return (

    <div className="staffEditPageShell min-h-screen bg-slate-100">
      <div className="staffEditPageWide staff-edit-page-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">

          <div className="flex items-center gap-3 min-w-0">

            <button

              type="button"

              onClick={() => navigate("/manager/manage/staff")}

              className="h-10 px-4 rounded-full border border-white/60 text-white text-sm md:text-base font-semibold transition hover:bg-white/10 active:bg-white/20"

            >

              Back to Staff
            </button>

            <h1 className="text-xl md:text-2xl font-semibold text-white truncate">

              Edit Staff

            </h1>

          </div>

          <div className="flex items-center gap-2">
            <ModalCloseX onClick={() => navigate("/manager/manage/staff")} />
          </div>
        </div>
        <div className="px-6 py-6 text-lg md:text-xl font-semibold text-slate-900">
          {headerLine}
        </div>
        {staff && !loading && (
          <div className="px-6 pb-2 space-y-4">
            {(linkedSalonIds.length > 0 || linkedBusinessIds.length > 0) && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">
                  Linked IDs
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  Shows the salon and business IDs linked to this staff member or linked user.
                </div>
                <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Salon IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedSalonIds.length > 0 ? (
                        linkedSalonIds.map((id) => (
                          <span
                            key={`linked-salon-${id}`}
                            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700"
                          >
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">None</span>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Business IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedBusinessIds.length > 0 ? (
                        linkedBusinessIds.map((id) => (
                          <span
                            key={`linked-business-${id}`}
                            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700"
                          >
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {canViewStaffHelp && (
              <ReportHelpCard
                title="Help Guide: Staff Setup"
                subtitle="Use this guide to set up login, details, schedule, services, and permissions quickly."
                sections={helpSections}
                troubleshooting={STAFF_GUIDE_TROUBLESHOOTING}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            )}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-xs uppercase tracking-wide text-slate-400">
                Quick Links
              </span>
              {staff?.user_id && (
                <button
                  type="button"
                  onClick={() => jumpToSectionWithHighlight("section-login")}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
                >
                  Login
                </button>
              )}
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-details")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-settings")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Settings
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-profile")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Profile
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-schedule")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Schedule
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-services")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Services
              </button>
              {linkedUserId && (
                <button
                  type="button"
                  onClick={() => jumpToSectionWithHighlight("section-permissions")}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
                >
                  Permissions
                </button>
              )}
            </div>
          </div>
        )}
        <div className="px-6 pb-8 space-y-6">
          {loading ? (
            <div className="text-slate-600">Loading...</div>
          ) : !staff ? (
            <div className="text-slate-600">Staff not found.</div>

          ) : (

            content

          )}
        </div>
        </div>
      </div>
    </div>
  );
}


function StaffPermissionsSection({
  userId,
  editorRole,
  token,
  onSaved,
  uiClasses = {},
  highlightSectionId = "",
  showInlineHelp = false,
}) {
  const normalizedEditorRole = (editorRole || "").toLowerCase();

  const isManagerEditor = normalizedEditorRole === "manager";

  const [permissionsBundle, setPermissionsBundle] = useState(null);

  const [overrides, setOverrides] = useState({});

  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const [error, setError] = useState("");

  const [status, setStatus] = useState("");

  const [saving, setSaving] = useState(false);

  const [resetting, setResetting] = useState(false);



  const targetRole = normalizeSalonRole(

    permissionsBundle?.role || "technician"

  );

  const disableAll = isManagerEditor && targetRole === "owner";



  const loadPermissions = useCallback(async () => {

    if (!userId) {

      return;

    }

    setLoadingPermissions(true);

    setError("");

    try {

      const bundle = await getUserPermissions(userId, token);
      setPermissionsBundle(bundle);

      setOverrides(bundle.overrides || {});

      setStatus("");

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to load permissions. Please check your access and try again."
        )
      );

    } finally {

      setLoadingPermissions(false);

    }

  }, [userId, token]);


  useEffect(() => {

    setPermissionsBundle(null);

    setOverrides({});

    setStatus("");

    if (userId) {

      loadPermissions();

    }

  }, [userId, loadPermissions]);



  const effectivePermissions = useMemo(() => {

    const base = { ...(permissionsBundle?.defaults || {}) };

    Object.entries(overrides || {}).forEach(([key, value]) => {

      base[key] = value;

    });

    return base;

  }, [permissionsBundle?.defaults, overrides]);



  const hasChanges = useMemo(

    () =>

      JSON.stringify(overrides || {}) !==

      JSON.stringify(permissionsBundle?.overrides || {}),

    [overrides, permissionsBundle?.overrides]

  );



  const togglePermission = (key) => {

    if (disableAll) return;

    setStatus("");

    setOverrides((prev) => {

      const next = { ...(prev || {}) };

      const defaultValue = permissionsBundle?.defaults?.[key] ?? false;

      const currentValue = effectivePermissions[key];

      const nextValue = !currentValue;

      if (nextValue === defaultValue) {

        delete next[key];

      } else {

        next[key] = nextValue;

      }

      return next;

    });

  };



  const handleSave = async () => {

    if (!userId) return;

    setSaving(true);

    setError("");

    try {

      const result = await updateUserPermissions(userId, overrides, token);
      setPermissionsBundle(result);

      setOverrides(result.overrides || {});

      setStatus("Permissions saved");

      onSaved?.();

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to save permissions. Please check your access and try again."
        )
      );

    } finally {

      setSaving(false);

    }

  };



  const handleReset = async () => {

    if (!userId) return;

    setResetting(true);

    setError("");

    try {

      const result = await resetUserPermissions(userId, token);
      setPermissionsBundle(result);

      setOverrides(result.overrides || {});

      setStatus("Permissions reset");

      onSaved?.();

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to reset permissions. Please check your access and try again."
        )
      );

    } finally {

      setResetting(false);

    }

  };



  if (!userId) {

    return null;

  }



  const {
    modalText = "text-base md:text-lg",
    btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full",
    cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6",
  } = uiClasses;
  const highlightedSectionClass =
    highlightSectionId === "section-permissions"
      ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
      : "";

  return (
    <div className={`${cardClass} ${modalText} ${highlightedSectionClass} space-y-4`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">Permissions</h2>
            {showInlineHelp && (
              <InfoTooltip
                text="Grant only what is needed for this role."
                label="Permission groups"
                maxWidth={260}
              />
            )}
          </div>
          <p className="text-sm md:text-base text-slate-600">
            Adjust the effective permissions for this user.
          </p>
        </div>
        {(saving || resetting) && (

          <span className="text-xs md:text-sm text-slate-500">

            {saving ? "Saving..." : "Resetting..."}

          </span>
        )}
      </div>
      {showInlineHelp && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          Tip: Start with role defaults, then add only the minimum extra access needed.
        </div>
      )}

      {loadingPermissions ? (
        <p className="text-sm md:text-base text-slate-500">Loading permissions...</p>

      ) : error ? (

        <p className="text-sm md:text-base text-rose-600">{error}</p>

      ) : (

        <>

          {PERMISSION_GROUPS.map((group) => (

            <div key={group.title} className="space-y-2">

              <div className="text-sm md:text-base font-semibold text-slate-900 uppercase tracking-wide">

                {group.title}

              </div>

              <div className="grid gap-2 sm:grid-cols-2">

                {group.items.map((item) => {

                  const isOwnerOnly = OWNER_ONLY_PERMISSIONS.includes(item.key);

                  const disabled = disableAll || (isManagerEditor && isOwnerOnly);

                  return (

                    <label

                      key={item.key}

                      className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm md:text-base ${disabled

                          ? "border-slate-200 bg-slate-50 text-slate-400"

                          : "border-slate-200 bg-white text-slate-700"

                        }`}

                    >

                      <span>{item.label}</span>

                      <input

                        type="checkbox"

                        checked={!!effectivePermissions[item.key]}

                        onChange={() => togglePermission(item.key)}

                        disabled={disabled}

                        className="h-4 w-4 text-slate-900 border-slate-300 rounded"

                      />

                    </label>

                  );

                })}

              </div>

            </div>

          ))}

          {status && (

            <p className="text-sm md:text-base text-emerald-600">{status}</p>

          )}

          <div className="flex flex-wrap gap-2">

            <button

              type="button"

              onClick={handleSave}

              disabled={!hasChanges || saving || disableAll}

              className={`${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800 disabled:opacity-50`}

            >

              {saving ? "Saving..." : "Save Permissions"}

            </button>

            <button

              type="button"

              onClick={handleReset}

              disabled={resetting || disableAll}

              className={`${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50`}

            >

              {resetting ? "Resetting..." : "Reset to defaults"}

            </button>

          </div>

        </>

      )}

    </div>

  );

}



export default function StaffEdit() {

  const { id } = useParams();

  const navigate = useNavigate();

  const { token, role, user } = useAuth();
  const normalizedRole = (role || "").toLowerCase();
  const { permissions: effectivePermissions, loading: permissionsLoading } =
    useMyPermissions({ enabled: Boolean(token && user?.salon_id) });
  const canAccessStaffEdit =
    ["admin", "host"].includes(normalizedRole) ||
    Boolean(user?.is_super_admin) ||
    (effectivePermissions === null
      ? MANAGER_ROLES.includes(normalizedRole)
      : Boolean(effectivePermissions?.["staff.manage"]));


  useEffect(() => {

    if (!token) {

      navigate("/login?next=/manager/manage/staff", { replace: true });

      return;

    }

    if (permissionsLoading || effectivePermissions === null) {
      return;
    }

    if (!canAccessStaffEdit) {
      navigate("/my-bookings", { replace: true });

    }

  }, [
    token,
    navigate,
    permissionsLoading,
    effectivePermissions,
    canAccessStaffEdit,
  ]);


  return <StaffEditPanel staffId={id} />;

}
